import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { httpRequest } from "../../utils/httpRequest";
import CustomEditor from "../common/Editor";
import ExplanationField from "./ExplanationField";

const makeEmptyForm = () => ({
  stem_fa: "",
  stem_en: "",
  prepTimeSec: 10,
  responseTimeSec: 60,
  capture_minSec: 10,
  capture_maxSec: 60,
  rubric_fa: "",
  rubric_en: "",
  points: 1,
  status: "published",
  layout: "top-bottom",
  tags: "",
  explanation_fa: "",
  explanation_en: "",
});

const itemToForm = (item) => {
  if (!item) return makeEmptyForm();

  const stem_fa = item?.stem?.fa || "";
  const stem_en = item?.stem?.en || "";
  const body = item?.body || {};
  const timings = body.timings || {};
  const capture = body.capture || {};
  const rubric = body.rubric || {};

  const explanation_fa = item?.explanation?.fa || "";
  const explanation_en = item?.explanation?.en || "";

  return {
    stem_fa,
    stem_en,
    prepTimeSec:
      typeof timings.prepTimeSec === "number" ? timings.prepTimeSec : 10,
    responseTimeSec:
      typeof timings.responseTimeSec === "number"
        ? timings.responseTimeSec
        : 60,
    capture_minSec: typeof capture.minSec === "number" ? capture.minSec : 10,
    capture_maxSec:
      typeof capture.maxSec === "number"
        ? capture.maxSec
        : timings.responseTimeSec || 60,
    rubric_fa: rubric.fa || "",
    rubric_en: rubric.en || "",
    points:
      typeof item.points === "number" && !Number.isNaN(item.points)
        ? item.points
        : 1,
    status: item.status || "published",
    layout: body.layout || "top-bottom",
    tags: Array.isArray(item.tags) ? item.tags.join(",") : "",
    explanation_fa,
    explanation_en,
  };
};

export default function AudioPromptModal({
  isOpen,
  onClose,
  token,
  onCreated,
  mode = "create", // "create" | "edit"
  item = null,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");
  const isEdit = mode === "edit" && item;

  const [form, setForm] = useState(makeEmptyForm());

  React.useEffect(() => {
    if (!isOpen) return;
    if (isEdit) {
      setForm(itemToForm(item));
    } else {
      setForm(makeEmptyForm());
    }
  }, [isOpen, isEdit, item]);

  if (!isOpen) return null;

  const handleEditorChange = (field, content) => {
    setForm((f) => ({ ...f, [field]: content }));
  };


  const submit = async (e) => {
    e.preventDefault();

    if (!Number(form.responseTimeSec)) {
      toast.error(t("audio_prompt.toasts.response_time_required"));
      return;
    }

    const payload = {
      kind: "audio_prompt",
      explanation: {
        fa: form.explanation_fa || undefined,
        en: form.explanation_en || undefined,
      },
      stem: { fa: form.stem_fa || undefined, en: form.stem_en || undefined },
      body: {
        timings: {
          prepTimeSec: Number(form.prepTimeSec) || 0,
          responseTimeSec: Number(form.responseTimeSec) || 60,
        },
        capture: {
          type: "record",
          minSec: Number(form.capture_minSec) || 0,
          maxSec:
            Number(form.capture_maxSec) || Number(form.responseTimeSec) || 60,
        },
        rubric: {
          fa: form.rubric_fa || undefined,
          en: form.rubric_en || undefined,
        },
        layout: form.layout,
      },
      points: Number(form.points) || 1,
      status: form.status || "draft",
      tags: form.tags
        ? form.tags
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    };

    try {
      const isEditMode = isEdit && item?._id;
      const url = isEditMode ? `/item/${item._id}` : "/item";
      const method = isEditMode ? "PUT" : "POST";

      const res = await httpRequest(method, url, payload, {
        "x-access-token": token,
      });
      const doc = res?.data?.data || res?.data;
      if (!doc?._id) throw new Error(res?.message || "FAILED");
      toast.success(
        isEditMode
          ? t("audio_prompt.toasts.updated")
          : t("audio_prompt.toasts.created")
      );
      onCreated?.(doc);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    }
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      {/* Stem */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div dir={isRtl ? "rtl" : "ltr"}>
          <label className="block text-sm font-medium mb-1">
            {t("audio_prompt.stem_fa")}
          </label>
          <CustomEditor
            value={form.stem_fa}
            onEditorChange={(content) => handleEditorChange("stem_fa", content)}
            placeholder={t("audio_prompt.stem_fa_ph")}
            directionality={isRtl ? "rtl" : "ltr"}
          />
        </div>
        <div dir="ltr">
          <label className="block text-sm font-medium mb-1">
            {t("audio_prompt.stem_en")}
          </label>
          <CustomEditor
            value={form.stem_en}
            onEditorChange={(content) => handleEditorChange("stem_en", content)}
            placeholder={t("audio_prompt.stem_en_ph")}
            directionality={isRtl ? "rtl" : "ltr"}
          />
        </div>
      </div>

      {/* Layout Selector */}
      <div>
        <label className="block text-sm font-medium mb-1">
          {t("select_sentence.layout_label")}
        </label>
        <div className="flex items-center flex-wrap gap-4 border p-3 rounded-lg bg-gray-50">
          {["top-bottom", "bottom-top", "left-right", "right-left"].map(
            (layout) => (
              <div key={layout} className="flex items-center">
                <input
                  type="radio"
                  id={`layout-${layout}`}
                  name="layout"
                  value={layout}
                  checked={form.layout === layout}
                  onChange={onChange}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                />
                <label
                  htmlFor={`layout-${layout}`}
                  className="ml-2 text-sm text-gray-700"
                >
                  {t(`select_sentence.layout_question_answer_${layout}`)}
                </label>
              </div>
            )
          )}
        </div>
      </div>

      {/* Timings */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("audio_prompt.prep_time_sec")}
          </label>
          <input
            type="number"
            min={0}
            className="w-full border rounded-lg px-3 py-2"
            value={form.prepTimeSec}
            onChange={(e) =>
              setForm((f) => ({ ...f, prepTimeSec: e.target.value }))
            }
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("audio_prompt.response_time_sec")}
          </label>
          <input
            type="number"
            min={5}
            className="w-full border rounded-lg px-3 py-2"
            value={form.responseTimeSec}
            onChange={(e) =>
              setForm((f) => ({ ...f, responseTimeSec: e.target.value }))
            }
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("audio_prompt.min_sec")}
            </label>
            <input
              type="number"
              min={0}
              className="w-full border rounded-lg px-3 py-2"
              value={form.capture_minSec}
              onChange={(e) =>
                setForm((f) => ({ ...f, capture_minSec: e.target.value }))
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("audio_prompt.max_sec")}
            </label>
            <input
              type="number"
              min={1}
              className="w-full border rounded-lg px-3 py-2"
              value={form.capture_maxSec}
              onChange={(e) =>
                setForm((f) => ({ ...f, capture_maxSec: e.target.value }))
              }
            />
          </div>
        </div>
      </div>

      {/* Rubric */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div dir={isRtl ? "rtl" : "ltr"}>
          <label className="block text-sm font-medium mb-1">
            {t("audio_prompt.rubric_fa")}
          </label>
          <CustomEditor
            value={form.rubric_fa}
            onEditorChange={(content) =>
              handleEditorChange("rubric_fa", content)
            }
            placeholder={t("audio_prompt.rubric_fa_ph")}
            directionality={isRtl ? "rtl" : "ltr"}
          />
        </div>
        <div dir="ltr">
          <label className="block text-sm font-medium mb-1">
            {t("audio_prompt.rubric_en")}
          </label>
          <CustomEditor
            value={form.rubric_en}
            onEditorChange={(content) =>
              handleEditorChange("rubric_en", content)
            }
            placeholder={t("audio_prompt.rubric_en_ph")}
            directionality={isRtl ? "rtl" : "ltr"}
          />
        </div>
      </div>

      {/* Meta */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("audio_prompt.points")}
          </label>
          <input
            type="number"
            min={0}
            className="w-full border rounded-lg px-3 py-2"
            value={form.points}
            onChange={(e) => setForm((f) => ({ ...f, points: e.target.value }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("audio_prompt.status")}
          </label>
          <select
            className="w-full border rounded-lg px-3 py-2"
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
          >
            <option value="published">{t("common.published")}</option>
            <option value="draft">{t("common.draft")}</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("audio_prompt.tags")}
          </label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            placeholder={t("common.tags_csv")}
            value={form.tags}
            onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
          />
        </div>
      </div>

      <ExplanationField
        valueFa={form.explanation_fa}
        valueEn={form.explanation_en}
        onChangeFa={(v) => handleEditorChange("explanation_fa", v)}
        onChangeEn={(v) => handleEditorChange("explanation_en", v)}
        isRtl={isRtl}
      />

      <button
        type="submit"
        className="px-4 py-2 bg-blue-600 text-white rounded-lg"
      >
        {isEdit ? t("common.update") : t("common.create")}
      </button>
    </form>
  );
}
